import React, { useEffect, useRef } from 'react';
import styles from './NotificationModal.module.scss';
import { createPortal } from 'react-dom';
import { phoneIconBlack, modal_background } from '../../assets/hostedassets';

export type NotificationPosition = 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'center';

interface NotificationModalProps {
    isOpen: boolean;
    onClose?: () => void;
    header: string | React.ReactNode;
    content: string | React.ReactNode;
    footer?: React.ReactNode;
    position?: NotificationPosition;
}

const POSITION_STYLES: Record<NotificationPosition, React.CSSProperties> = {
    'top-right': { top: '5rem', right: '3rem' },
    'top-left': { top: '5rem', left: '3rem' },
    'bottom-right': { bottom: '5rem', right: '3rem' },
    'bottom-left': { bottom: '5rem', left: '3rem' },
    center: { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' },
};

const NotificationModal: React.FC<NotificationModalProps> = ({ isOpen, onClose, header, content, footer, position = 'center' }) => {
    const modalRef = useRef<HTMLDivElement>(null);

    // Handle Escape key
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && onClose) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            return () => document.removeEventListener('keydown', handleEscape);
        }
    }, [isOpen, onClose]);

    // Focus management
    useEffect(() => {
        if (isOpen && modalRef.current) {
            const previouslyFocusedElement = document.activeElement as HTMLElement;
            modalRef.current.focus();

            return () => {
                previouslyFocusedElement?.focus();
            };
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget && onClose) {
            onClose();
        }
    };

    return createPortal(
        <>
            <div className={styles.Backdrop} onClick={handleBackdropClick} />
            <div
                className={styles.NotificationModalContainer}
                style={{
                    position: 'fixed',
                    zIndex: 10000,
                    ...POSITION_STYLES[position],
                }}
            >
                <div
                    className={styles.NotificationModal}
                    style={{ backgroundImage: `url(${modal_background})` }}
                    role="dialog"
                    aria-modal="true"
                    ref={modalRef}
                    tabIndex={-1}
                >
                    {onClose && (
                        <button className={styles.CloseButton} onClick={onClose} aria-label="Close">
                            &times;
                        </button>
                    )}

                    <div className={styles.HeaderContainer}>
                        <img src={phoneIconBlack} alt="Phone" className={styles.PhoneIcon} />
                        <div className={styles.HeaderText}>{header}</div>
                    </div>

                    <div className={styles.Content}>{content}</div>

                    {footer && <div className={styles.Footer}>{footer}</div>}
                </div>
            </div>
        </>,
        document.body,
    );
};

export default NotificationModal;
